import React, { useEffect, useState } from 'react';
import SubmitButton from '../formComponents/SubmitButton';
import ErrorMessage from '../formComponents/ErrorMessage';
import { GIPHY_RATINGS } from '../../../utils/giphy';
import { normalizeTeamBoardGifSettings } from '../../../utils/gifSettings';

const EMPTY_SETTINGS = {};
const EMPTY_BOARD_SETTINGS = {};

export default function EditBoardGifSettingsForm({
  isOpen,
  initialSettings = EMPTY_SETTINGS,
  initialBoardSettings = EMPTY_BOARD_SETTINGS,
  onClose,
  onSave,
}) {
  const [settings, setSettings] = useState(() => normalizeTeamBoardGifSettings(initialSettings));
  const [boardSettings, setBoardSettings] = useState(() => ({
    memberDeleteReviewEnabled: initialBoardSettings?.memberDeleteReviewEnabled !== false,
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSettings(normalizeTeamBoardGifSettings(initialSettings));
    setBoardSettings({
      memberDeleteReviewEnabled: initialBoardSettings?.memberDeleteReviewEnabled !== false,
    });
    setSaving(false);
    setError('');
  }, [initialBoardSettings, initialSettings, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      await onSave?.({
        gifs: settings,
        boards: boardSettings,
      });
      onClose?.();
    } catch (saveError) {
      setError(saveError?.message || 'Could not save board settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="modal-form" onSubmit={handleSubmit}>
      <h2>Board Settings</h2>
      <p className="text-muted">
        Set the default GIPHY policy for message boards. Individual boards can inherit this or set their own override.
      </p>
      {error && <ErrorMessage message={error} />}

      <label className="form-group">
        <span className="form-label">Board GIFs</span>
        <select
          className="form-input"
          disabled={saving}
          value={settings.boardsEnabled ? 'enabled' : 'disabled'}
          onChange={(event) => setSettings((current) => ({
            ...current,
            boardsEnabled: event.target.value === 'enabled',
          }))}
        >
          <option value="enabled">Enabled by default</option>
          <option value="disabled">Disabled by default</option>
        </select>
      </label>

      <label className="form-group">
        <span className="form-label">Default max rating</span>
        <select
          className="form-input"
          disabled={saving}
          value={settings.boardDefaultRating}
          onChange={(event) => setSettings((current) => ({ ...current, boardDefaultRating: event.target.value }))}
        >
          {GIPHY_RATINGS.map((rating) => (
            <option key={rating} value={rating}>{rating.toUpperCase()}</option>
          ))}
        </select>
      </label>

      <div className="form-group">
        <span className="form-label">Member deletes</span>
        <label className="checkbox-row">
          <input
            type="checkbox"
            disabled={saving}
            checked={boardSettings.memberDeleteReviewEnabled}
            onChange={(event) => setBoardSettings((current) => ({
              ...current,
              memberDeleteReviewEnabled: event.target.checked,
            }))}
          />
          <span>Send member-deleted topics and comments to the club review queue</span>
        </label>
        <p className="text-muted">
          Deleted content is always removed immediately and retained as moderation evidence.
        </p>
      </div>

      <div className="form-actions">
        <SubmitButton type="button" onClick={onClose} label="Cancel" variant="ghost" size="sm" disabled={saving} />
        <SubmitButton type="submit" label={saving ? 'Saving…' : 'Save Settings'} variant="primary" disabled={saving} />
      </div>
    </form>
  );
}
